import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Gift, Copy, Check, Users, BadgeCheck, Clock, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { formatDateOnly } from '../../lib/format'
import { Breadcrumb } from '../../components/Breadcrumb'
import { DataTable } from '../../components/table/DataTable'
import { referralMeResponse } from '../../lib/types'
import { copyText } from '../../lib/clipboard'

const REFERRAL_COLUMNS = [
  {
    key: 'company',
    header: 'Company',
    grow: true,
    pinnable: true,
    minWidth: 180,
    sortAccessor: (r) => (r.company ?? '').toLowerCase(),
    searchAccessor: (r) => `${r.company ?? ''} ${r.email ?? ''}`,
    exportValue: (r) => r.company ?? '',
    render: (r) => (
      <div className="min-w-0">
        <p className="font-medium text-[color:var(--color-ink-strong)] truncate">
          {r.company || '—'}
        </p>
        {r.email ? (
          <p className="text-xs text-[color:var(--color-ink-muted)] truncate">
            {r.email}
          </p>
        ) : null}
      </div>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    minWidth: 110,
    sortAccessor: (r) => r.status ?? '',
    exportValue: (r) => STATUS_STYLES[r.status]?.label ?? r.status ?? '',
    render: (r) => <StatusPill status={r.status} />,
  },
  {
    key: 'reward',
    header: 'Reward',
    minWidth: 110,
    sortAccessor: (r) => Number(r.reward?.days ?? 0),
    exportValue: (r) => (r.reward ? `+${r.reward.days} days` : ''),
    render: (r) =>
      r.reward ? (
        <span
          className="inline-flex items-center gap-1.5 text-xs text-[color:var(--color-ink)]"
          title={
            r.reward.until
              ? `Your plan now runs until ${formatDateOnly(r.reward.until)}`
              : undefined
          }
        >
          <Gift size={14} className="text-green-600" />
          +{r.reward.days} days
        </span>
      ) : (
        <span className="text-[color:var(--color-ink-muted)]">—</span>
      ),
  },
  {
    key: 'createdAt',
    header: 'Date',
    minWidth: 120,
    sortAccessor: (r) => (r.createdAt ? new Date(r.createdAt) : null),
    exportValue: (r) =>
      r.createdAt ? formatDateOnly(r.createdAt) : '',
    cellClassName: 'text-[color:var(--color-ink-muted)] whitespace-nowrap',
    render: (r) =>
      r.createdAt ? formatDateOnly(r.createdAt) : '—',
  },
]

/** Human-readable reward blurb from the program config — a free plan
 *  extension, applied immediately to the referrer's subscription. */
function rewardText(config) {
  if (!config) return 'a free plan extension'
  const d = config.extensionDays
  if (d === 30) return 'one free month'
  return `a free ${d}-day plan extension`
}

const STATUS_STYLES = {
  pending: { label: 'Pending', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  qualified: { label: 'Qualified', cls: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300' },
  rewarded: { label: 'Rewarded', cls: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' },
  void: { label: 'Void', cls: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400' },
}

function StatusPill({ status }) {
  const s = STATUS_STYLES[status] ?? STATUS_STYLES.void
  return (
    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${s.cls}`}>
      {s.label}
    </span>
  )
}

function StatCard({ icon: Icon, label, value, tone }) {
  return (
    <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-4 flex items-center gap-3">
      <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${tone}`}>
        <Icon size={18} />
      </div>
      <div>
        <p className="text-2xl font-semibold leading-none text-[color:var(--color-ink-strong)]">
          {value}
        </p>
        <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">{label}</p>
      </div>
    </div>
  )
}

export function ReferralsPage() {
  const [copiedValue, setCopiedValue] = useState(null)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['referrals', 'me'],
    queryFn: async () => {
      const res = await api.get('/referrals/me')
      return referralMeResponse.parse(res.data)
    },
  })

  const copied = copiedValue !== null && copiedValue === data?.link

  const copyLink = async () => {
    if (!data?.link) return
    // Routed through the shared helper so a refused/unavailable Clipboard API
    // falls back to a gesture-based execCommand copy, and the label stays on
    // "Copied" rather than reverting (product decision — a confirmation that
    // erases itself left users unsure the copy had happened). See lib/clipboard.js.
    if (!(await copyText(data.link))) {
      toast.error('Could not copy — select and copy the link manually.')
      return
    }
    setCopiedValue(data.link)
    toast.success('Referral link copied.')
  }

  return (
    <div>
      <Breadcrumb current="Refer & Earn" />

      <header className="mb-6">
        <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] flex items-center gap-3">
          <Gift size={20} className="text-[color:var(--color-accent-strong)]" />
          Refer &amp; Earn
        </h1>
        <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
          Invite another company to TaskMagix and earn {data ? rewardText(data.config) : 'a free plan extension'} when they sign up and start a trial or paid plan.
        </p>
      </header>

      {isLoading ? (
        <p className="text-sm text-[color:var(--color-ink-muted)] inline-flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </p>
      ) : isError || !data ? (
        <p className="text-sm text-[color:var(--color-priority-urgent)]">
          Couldn&apos;t load your referral details. Please refresh and try again.
        </p>
      ) : (
        <div className="max-w-4xl space-y-6">
          {data.config.enabled === false && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-300">
              The referral programme isn&apos;t active right now. Your link still works —
              sign-ups are tracked and rewards are issued once the programme is turned on.
            </div>
          )}

          {/* Share card */}
          <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5 space-y-3">
            <h2 className="text-sm font-semibold text-[color:var(--color-ink-strong)]">
              Your referral link
            </h2>
            <div className="flex flex-col gap-2 sm:flex-row">
              <input
                readOnly
                // The only visible text is the <h2> above the row, so without
                // a name of its own this announces as a bare read-only textbox.
                aria-label="Your referral link"
                value={data.link}
                onFocus={(e) => e.target.select()}
                className="flex-1 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] px-3 py-2 text-sm font-mono text-[color:var(--color-ink)]"
              />
              <button
                type="button"
                onClick={copyLink}
                className="shrink-0 inline-flex items-center justify-center gap-1.5 rounded-md bg-[color:var(--color-primary)] px-4 py-2 text-sm font-medium text-white hover:bg-[color:var(--color-primary-hover)] cursor-pointer"
              >
                {copied ? <Check size={16} /> : <Copy size={16} />}
                {copied ? 'Copied' : 'Copy Link'}
              </button>
            </div>
            <p className="text-sm text-[color:var(--color-ink-muted)]">
              Your code:{' '}
              <span className="font-mono font-semibold text-[color:var(--color-ink-strong)]">
                {data.code}
              </span>
            </p>
          </div>

          {/* Stats — Qualified is a meaningful state: earned, pending issue. */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <StatCard icon={Clock} label="Pending" value={data.stats.pending} tone="bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" />
            <StatCard icon={BadgeCheck} label="Qualified" value={data.stats.qualified} tone="bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" />
            <StatCard icon={Gift} label="Rewarded" value={data.stats.rewarded} tone="bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300" />
          </div>

          {/* How it works */}
          <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5">
            <h2 className="text-sm font-semibold text-[color:var(--color-ink-strong)] mb-4">
              How it works
            </h2>
            <ol className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {[
                { n: 1, t: 'Share your link', d: 'Send your referral link to another company.' },
                { n: 2, t: 'They join', d: 'They sign up and start a trial or paid plan.' },
                { n: 3, t: 'You earn', d: `You earn ${data ? rewardText(data.config) : 'a referral credit'}.` },
              ].map((step) => (
                <li key={step.n} className="flex gap-3">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[color:var(--color-accent-strong)]/10 text-sm font-semibold text-[color:var(--color-accent-strong)]">
                    {step.n}
                  </span>
                  <div>
                    <p className="font-medium text-[color:var(--color-ink-strong)]">{step.t}</p>
                    <p className="text-sm text-[color:var(--color-ink-muted)]">{step.d}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          {/* Referral list — shared DataTable (client-side): search / sort /
           *  export over your own referrals. */}
          <div>
            <h2 className="text-sm font-semibold text-[color:var(--color-ink-strong)] mb-3">
              Your referrals
            </h2>
            <DataTable
              tableId="app-referrals"
              columns={REFERRAL_COLUMNS}
              rows={data.referrals}
              getRowId={(r) => r.id}
              exportBaseName="referrals"
              exportTitle="Referrals"
              searchPlaceholder="Search by Company"
              defaultSort={[{ key: 'createdAt', dir: 'desc' }]}
              emptyState={{
                icon: Users,
                title: 'No referrals yet',
                body: 'Share your link to get started.',
              }}
            />
          </div>
        </div>
      )}
    </div>
  )
}
